import { computed, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { environment } from '@dxp/core/environment';
import {
    DefaultTenant,
    IRbacTenant,
    ITelXLKpiQueueThreshold,
    ITenant,
    IUser,
    mergeMissingQueueThresholds,
    TelXLKpiQueueThresholdDefaultState,
    Tenant,
} from '@dxp/core/models';
import {
    signalStore,
    withComputed,
    withFeature,
    withHooks,
    withMethods,
    withState,
} from '@ngrx/signals';
import {
    catchError,
    combineLatest,
    distinctUntilChanged,
    filter,
    forkJoin,
    map,
    of,
    switchMap,
    tap,
} from 'rxjs';
import { updateState } from '@angular-architects/ngrx-toolkit';
import { RbacApiService } from '@dxp/core/api';
import { ReloadService } from '@dxp/core/services';
import { BUSINESSUNIT_STORE } from '../businessunit/businessunit.token';
import { withBusinessUnits } from '../businessunit/businessunit.feature';
import { USER_STORE, withUser } from '../../user';

interface ITenantStoreState {
    tenantId: IRbacTenant;
    kpi: ITelXLKpiQueueThreshold;
    loading: boolean;
}

const initialState: ITenantStoreState = {
    tenantId: DefaultTenant(''),
    kpi: TelXLKpiQueueThresholdDefaultState,
    loading: true,
};

export const TenantStore = signalStore(
    { providedIn: 'root' },
    environment.storeWithDevTools('TenantStore'),
    withFeature(() => withBusinessUnits(inject(BUSINESSUNIT_STORE))),
    withFeature(() => withUser(inject(USER_STORE))),
    withState<ITenantStoreState>(initialState),
    withComputed(store => ({
        ready: computed<boolean>(() => !store.loading()),
        tenant: computed<ITenant>(() => {
            return new Tenant(
                store.tenantId().id,
                store.tenantId().name,
                store.businessUnits(),
                store.kpi(),
            );
        }),
        getTenantKpi: computed<ITelXLKpiQueueThreshold>(() => {
            const kpi = store.kpi();
            return kpi ?? TelXLKpiQueueThresholdDefaultState;
        }),
    })),
    withMethods(store => ({
        updateTenant(tenant: IRbacTenant) {
            updateState(store, 'UPDATE_TENANT', state => {
                return {
                    ...state,
                    tenantId: tenant,
                };
            });
        },
        updateTenantKpi(telXLKpiThreshold: ITelXLKpiQueueThreshold) {
            mergeMissingQueueThresholds(telXLKpiThreshold);

            updateState(store, 'UPDATE_TENANT_KPI', state => {
                return {
                    ...state,
                    kpi: telXLKpiThreshold,
                    loading: false,
                };
            });
        },
    })),
    withHooks({
        onInit(
            store,
            rbacApiService = inject(RbacApiService),
            reloadService = inject(ReloadService),
            destroyRef = inject(DestroyRef),
        ) {
            combineLatest(
                toObservable(store.user),
                toObservable(reloadService.refreshTime),
            )
                .pipe(
                    map(([user, refresh]) => ({
                        tenantId: user.tenantId,
                        refresh,
                    })),
                    filter(
                        (x): x is { tenantId: string; refresh: number } =>
                            !!x.tenantId && x.tenantId.length > 0,
                    ),
                    distinctUntilChanged(
                        (a, b) =>
                            a.tenantId === b.tenantId &&
                            a.refresh === b.refresh,
                    ),
                    switchMap(({ tenantId }) =>
                        forkJoin({
                            tenant: rbacApiService.getTenant(tenantId).pipe(
                                catchError(error => {
                                    reloadService.error(
                                        'TENANT_STORE',
                                        error.message,
                                    );

                                    return of(DefaultTenant(tenantId));
                                }),
                            ),
                            kpiThresholds: rbacApiService
                                .getKpiThresholds(tenantId, 'tenants')
                                .pipe(catchError(() => of(undefined))),
                        }),
                    ),
                    tap(({ tenant, kpiThresholds }) => {
                        store.updateTenant(tenant);
                        store.updateTenantKpi(
                            (kpiThresholds?.kpiTelXLThreshold as ITelXLKpiQueueThreshold) ??
                                TelXLKpiQueueThresholdDefaultState,
                        );
                        store.updateUser({
                            tenant,
                        } as IUser);
                    }),
                    takeUntilDestroyed(destroyRef),
                )
                .subscribe();
        },
    }),
);
